import { useCallback, useEffect } from 'react';
// @mui
import Checkbox from '@mui/material/Checkbox';
import Stack from '@mui/material/Stack';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import FormControl from '@mui/material/FormControl';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { MultiInputDateRangeField } from '@mui/x-date-pickers-pro/MultiInputDateRangeField';
import { FieldOwnerState } from '@mui/x-date-pickers/models';
import { PickerRangeValue } from '@mui/x-date-pickers/internals';
// luxon
import { DateTime } from 'luxon';
// redux
import { useDispatch, useSelector } from 'src/redux/store';
import { setDataSource, resetReportRows, setFilter } from 'src/redux/slices/report';
// utils
import { capitalizeFirstLetter, spaceOnCapital } from 'src/utils/string-utils';
// components
import { useLocales } from 'src/locales';
// types
import { IReportTemplateTableFilters } from 'src/types/report-template';
// date shortcuts
import { shortcutItems } from './constants/date';
// data sources
import { enrtiesDataSource } from './constants/data-source/entries';
// injector
import { VariableInjector } from './variable-injector/variable-injector';

// ----------------------------------------------------------------------

type Props = {
  disableEdit?: boolean;
};

export default function ReportToolbar({ disableEdit = false }: Props) {
  const { t } = useLocales();

  const dispatch = useDispatch();

  const filters = useSelector((state) => state.report.filter) as IReportTemplateTableFilters;
  const report = useSelector((state) => state.report.report);
  const loading = useSelector((state) => state.report.loading[0]);
  const error = useSelector((state) => state.errorMessage.currentError);

  useEffect(() => {
    if (error) {
      dispatch(setFilter({ key: 'fromDate', filter: null }));
      dispatch(setFilter({ key: 'toDate', filter: null }));
    }
  }, [dispatch, error]);

  const handleTableFilterUpdate = useCallback(() => {
    report.tables!.forEach((x, i) => {
      VariableInjector(i);
    });
  }, [report.tables]);

  const handleFilterDate = useCallback(
    (newValue: PickerRangeValue) => {
      dispatch(setFilter({ key: 'fromDate', filter: newValue[0] }));
      dispatch(setFilter({ key: 'toDate', filter: newValue[1] }));
      handleTableFilterUpdate();
    },
    [dispatch, handleTableFilterUpdate]
  );

  const handleFilterType = useCallback(
    (event: SelectChangeEvent<string[]>) => {
      dispatch(setDataSource({ dataSource: [...event.target.value] }));
      dispatch(setFilter({ key: 'dataSource', filter: event.target.value }));
      if (event.target.value.length === 0) {
        dispatch(resetReportRows());
      } else {
        dispatch(resetReportRows([{ id: '' }]));
      }
    },
    [dispatch]
  );

  return (
    <Stack
      spacing={2}
      alignItems={{ xs: 'flex-end', md: 'center' }}
      direction={{
        xs: 'column',
        md: 'row',
      }}
      sx={{
        p: 2.5,
        pr: { xs: 2.5, md: 1 },
      }}
    >
      <Grid container alignItems="center" spacing={2} flexGrow={1} sx={{ width: 1 }}>
        <Grid size={{ xs: 12, md: 8 }}>
          <FormControl
            sx={{
              flexShrink: 1,
              width: { xs: 1, md: 1 },
            }}
          >
            <DateRangePicker
              slots={{ field: MultiInputDateRangeField }}
              slotProps={{
                shortcuts: {
                  items: shortcutItems.map((x) => ({ ...x, label: t(x.label) })),
                  sx: {
                    '& .MuiListItem-root': {
                      '& .MuiButtonBase-root': {
                        backgroundColor: (theme) => theme.palette.primary.main,
                      },
                    },
                  },
                },
                textField: ({ position }: FieldOwnerState & { position?: 'start' | 'end' }) => ({
                  label: position === 'start' ? t('From date') : t('To date'),
                }),
              }}
              label={[t('From date'), t('To date')]}
              value={[filters.fromDate, filters.toDate]}
              onChange={handleFilterDate}
            />
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <FormControl
            sx={{
              flexShrink: 1,
              width: { xs: 1, md: 1 },
            }}
          >
            <InputLabel>{t('Data source')}</InputLabel>
            <Select
              disabled={disableEdit || loading}
              multiple
              value={filters.dataSource}
              onChange={handleFilterType}
              input={<OutlinedInput label={t('Data source')} />}
              renderValue={(selected) =>
                enrtiesDataSource
                  .filter((x) => selected.includes(x))
                  .map((value: string) => t(capitalizeFirstLetter(spaceOnCapital(value))))
                  .join(', ')
              }
              sx={{ textTransform: 'capitalize' }}
            >
              {enrtiesDataSource.map((option, index) => (
                <MenuItem
                  key={`datasource-${index}`}
                  value={option}
                  sx={{
                    justifyContent: 'space-between',
                  }}
                >
                  <Checkbox
                    disableRipple
                    size="small"
                    checked={filters.dataSource.includes(option)}
                  />
                  <Typography
                    sx={{
                      width: '100%',
                    }}
                  >
                    {t(capitalizeFirstLetter(spaceOnCapital(option)))}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Stack>
  );
}
